import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "w-full space-y-4" }
const _hoisted_2 = { class: "font-semibold text-xl text-center" }
const _hoisted_3 = { class: "text-center font-semibold" }
const _hoisted_4 = {
  key: 0,
  class: "flex items-center justify-center gap-2 py-10"
}
const _hoisted_5 = { class: "font-medium" }
const _hoisted_6 = {
  key: 1,
  class: "py-10 text-center space-y-4"
}
const _hoisted_7 = { class: "text-sm text-red-600" }
const _hoisted_8 = {
  key: 2,
  class: "grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
}
const _hoisted_9 = { class: "flex flex-col rounded-lg border-2 border-black border-opacity-10" }
const _hoisted_10 = {
  key: 0,
  class: "border-b border-gray-200 p-2"
}
const _hoisted_11 = { class: "max-h-[32rem] overflow-y-auto divide-y divide-gray-100" }
const _hoisted_12 = ["aria-current", "onClick"]
const _hoisted_13 = {
  key: 0,
  class: "px-3 py-6 text-center text-sm text-gray-500"
}
const _hoisted_14 = {
  key: 0,
  class: "flex flex-col gap-4 rounded-lg border-2 border-black border-opacity-10 p-4"
}
const _hoisted_15 = { class: "flex flex-wrap items-baseline justify-between gap-2" }
const _hoisted_16 = { class: "font-semibold" }
const _hoisted_17 = {
  key: 0,
  class: "text-sm text-gray-500"
}
const _hoisted_18 = {
  key: 0,
  class: "text-sm text-gray-500"
}
const _hoisted_19 = {
  key: 1,
  class: "text-sm text-gray-500"
}
const _hoisted_20 = {
  key: 2,
  class: "max-h-[28rem] space-y-3 overflow-y-auto"
}
const _hoisted_21 = { class: "text-xs font-medium uppercase tracking-wide text-gray-500" }
const _hoisted_22 = { class: "flex flex-wrap gap-2" }
const _hoisted_23 = { class: "tabular-nums" }
const _hoisted_24 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_25 = { class: "mt-auto flex justify-end" }
const _hoisted_26 = { class: "flex justify-center gap-x-2" }

import { computed, ref, toRef, watch } from 'vue';
import { parseISO } from 'date-fns';
import { ChevronRight } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchInput } from '@/components/ui/input';
import Spinner from '@/components/ui/Spinner.vue';
import { availabilityOf, countDatesByMonth, hasBothDates, isDateSelectionComplete, reportRequestOf, toggleCountDate, type ReportDateSelection } from '@/composables/inventory/inventoryReport';
import { i18n } from '@/plugins/internationalization/i18n';
import { useMedicineCountDates } from '@/plugins/store/actions/queries/inventory';
import type { MedicineCountDatesFarm, MedicineReportRequestFarm } from '@/types';

/** From this many farms the list gets a search field; below it every farm is in view anyway. */
const SEARCH_FROM_FARMS = 8;


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectDatesStep',
  props: /*@__PURE__*/_mergeModels({
    farmIds: {}
  }, {
    "value": { required: true },
    "valueModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["next", "back"], ["update:value"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;

const selection = _useModel<ReportDateSelection>(__props, 'value');

const emit = __emit;

const { data, isLoading, isError, refetch } = useMedicineCountDates(toRef(props, 'farmIds'));

const farms = computed(() => data.value ?? []);
const canContinue = computed(() => isDateSelectionComplete(farms.value, selection.value));

const search = ref('');
const visibleFarms = computed(() => {
    const query = search.value.trim().toLocaleLowerCase();

    return query === '' ? farms.value : farms.value.filter((farm) => farm.farm_name.toLocaleLowerCase().includes(query));
});

function isPending(farm: MedicineCountDatesFarm): boolean {
    return availabilityOf(farm) === 'comparable' && !hasBothDates(selection.value, farm.farm_id);
}

/** The farm whose count days the card on the right shows. */
const activeFarmId = ref<number | null>(null);
const activeFarm = computed(() => farms.value.find((farm) => farm.farm_id === activeFarmId.value) ?? null);
const activeMonths = computed(() => (activeFarm.value ? countDatesByMonth(activeFarm.value.dates) : []));

// Nothing is picked up front; the card opens on the first farm still waiting for its days. Days picked before going back are kept.
watch(
    farms,
    (loaded) => {
        if (!loaded.some((farm) => farm.farm_id === activeFarmId.value)) {
            activeFarmId.value = (loaded.find(isPending) ?? loaded[0])?.farm_id ?? null;
        }
    },
    { immediate: true }
);

/** The next farm after the open one that still lacks its days, wrapping round; none once every farm has them. */
const nextFarm = computed(() => {
    const list = farms.value;
    const index = list.findIndex((farm) => farm.farm_id === activeFarmId.value);

    for (let offset = 1; offset < list.length; offset++) {
        const farm = list[(index + offset) % list.length];

        if (isPending(farm)) {
            return farm;
        }
    }

    return null;
});

/** The farm's line in the list: its period once both days are picked, otherwise what it is missing. */
function statusOf(farm: MedicineCountDatesFarm): { text: string; tone: string } {
    const availability = availabilityOf(farm);

    if (availability === 'single') {
        return { text: i18n.global.t('views.health.inventory.report.selectDates.onlyOneCount'), tone: 'text-gray-400' };
    }
    if (availability === 'none') {
        return { text: i18n.global.t('views.health.inventory.report.selectDates.noCount'), tone: 'text-gray-400' };
    }
    if (!hasBothDates(selection.value, farm.farm_id)) {
        return { text: i18n.global.t('views.health.inventory.report.selectDates.missing'), tone: 'text-warning-700' };
    }

    const [start, end] = selection.value[farm.farm_id];

    return { text: `${formatDate(start)} → ${formatDate(end)}`, tone: 'text-gray-700' };
}

function isPicked(farmId: number, date: string): boolean {
    return (selection.value[farmId] ?? []).includes(date);
}

/** A third day cannot be ticked while two are; unticking one frees the choice. */
function isLocked(farmId: number, date: string): boolean {
    return hasBothDates(selection.value, farmId) && !isPicked(farmId, date);
}

/** Which end of the period a picked day is, once both are picked: the earlier starts it. */
function roleOf(farmId: number, date: string): 'start' | 'end' | null {
    if (!hasBothDates(selection.value, farmId) || !isPicked(farmId, date)) {
        return null;
    }

    return selection.value[farmId][0] === date ? 'start' : 'end';
}

function toggle(farmId: number, date: string) {
    selection.value = { ...selection.value, [farmId]: toggleCountDate(selection.value[farmId] ?? [], date) };
}

/** Hands over the farms that can be compared, each with its two days. */
function show() {
    emit('next', reportRequestOf(farms.value, selection.value));
}

function formatDate(date: string): string {
    return i18n.global.d(parseISO(date), 'short');
}

function formatMonth(month: string): string {
    return i18n.global.d(parseISO(`${month}-01`), 'monthOfYearLong');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h2", _hoisted_2, _toDisplayString(_ctx.$t('views.health.inventory.report.selectDates.title')), 1),
    _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.$t('views.health.inventory.report.selectDates.description')), 1),
    (_unref(isLoading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t('views.health.inventory.report.loading')), 1),
          _createVNode(Spinner, { class: "size-5 animate-spin" })
        ]))
      : (_unref(isError))
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('views.pigs.records.errors.couldNotLoadData')), 1),
            _createVNode(_unref(Button), {
              variant: "outline",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(refetch)()))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('views.pigs.records.errors.retry')), 1)
              ]),
              _: 1
            })
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              (farms.value.length >= SEARCH_FROM_FARMS)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createVNode(_unref(SearchInput), {
                      modelValue: search.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
                      placeholder: _ctx.$t('views.health.inventory.report.selectDates.searchPlaceholder')
                    }, null, 8, ["modelValue", "placeholder"])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("ul", _hoisted_11, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFarms.value, (farm) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: farm.farm_id
                  }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: _normalizeClass(["flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left", farm.farm_id === activeFarmId.value ? 'bg-gray-100' : 'hover:bg-gray-50']),
                      "aria-current": farm.farm_id === activeFarmId.value ? 'true' : undefined,
                      onClick: ($event: any) => (activeFarmId.value = farm.farm_id)
                    }, [
                      _createElementVNode("span", {
                        class: _normalizeClass(["truncate font-medium", { 'text-gray-400': _unref(availabilityOf)(farm) !== 'comparable' }])
                      }, _toDisplayString(farm.farm_name), 3),
                      _createElementVNode("span", {
                        class: _normalizeClass(["whitespace-nowrap text-sm tabular-nums", statusOf(farm).tone])
                      }, _toDisplayString(statusOf(farm).text), 3)
                    ], 10, _hoisted_12)
                  ]))
                }), 128)),
                (visibleFarms.value.length === 0)
                  ? (_openBlock(), _createElementBlock("li", _hoisted_13, _toDisplayString(_ctx.$t('views.health.inventory.report.selectDates.noMatch')), 1))
                  : _createCommentVNode("", true)
              ])
            ]),
            (activeFarm.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  _createElementVNode("div", _hoisted_15, [
                    _createElementVNode("h4", _hoisted_16, _toDisplayString(activeFarm.value.farm_name), 1),
                    (_unref(availabilityOf)(activeFarm.value) === 'comparable')
                      ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_ctx.$t('views.health.inventory.report.selectDates.pickTwo')), 1))
                      : _createCommentVNode("", true)
                  ]),
                  (_unref(availabilityOf)(activeFarm.value) === 'single')
                    ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(_ctx.$t('views.health.inventory.report.selectDates.onlyOneCount')), 1))
                    : (_unref(availabilityOf)(activeFarm.value) === 'none')
                      ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(_ctx.$t('views.health.inventory.report.selectDates.noCount')), 1))
                      : (_openBlock(), _createElementBlock("div", _hoisted_20, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeMonths.value, (month) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: month.month,
                              class: "space-y-1.5"
                            }, [
                              _createElementVNode("div", _hoisted_21, _toDisplayString(formatMonth(month.month)), 1),
                              _createElementVNode("div", _hoisted_22, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(month.dates, (date) => {
                                  return (_openBlock(), _createElementBlock("label", {
                                    key: date,
                                    class: _normalizeClass(["flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5", isLocked(activeFarm.value.farm_id, date) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50'])
                                  }, [
                                    _createVNode(_unref(Checkbox), {
                                      checked: isPicked(activeFarm.value.farm_id, date),
                                      disabled: isLocked(activeFarm.value.farm_id, date),
                                      "onUpdate:checked": ($event: any) => (toggle(activeFarm.value.farm_id, date))
                                    }, null, 8, ["checked", "disabled", "onUpdate:checked"]),
                                    _createElementVNode("span", _hoisted_23, _toDisplayString(formatDate(date)), 1),
                                    (roleOf(activeFarm.value.farm_id, date))
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_24, _toDisplayString(_ctx.$t(`views.health.inventory.report.selectDates.${roleOf(activeFarm.value.farm_id, date)}`)), 1))
                                      : _createCommentVNode("", true)
                                  ], 2))
                                }), 128))
                              ])
                            ]))
                          }), 128))
                        ])),
                  _createElementVNode("div", _hoisted_25, [
                    _createVNode(_unref(Button), {
                      variant: "outline",
                      size: "sm",
                      disabled: nextFarm.value === null,
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (activeFarmId.value = nextFarm.value?.farm_id ?? activeFarmId.value))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('views.health.inventory.report.selectDates.nextFarm')) + " ", 1),
                        _createVNode(_unref(ChevronRight), {
                          class: "ml-1 size-4",
                          "aria-hidden": "true"
                        })
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ])
                ]))
              : _createCommentVNode("", true)
          ])),
    _createElementVNode("div", _hoisted_26, [
      _createVNode(_unref(Button), {
        variant: "outline",
        class: "min-w-32",
        onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('back')))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('views.health.inventory.report.back')), 1)
        ]),
        _: 1
      }),
      _createVNode(_unref(Button), {
        disabled: !canContinue.value,
        class: "min-w-32",
        onClick: show
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('views.health.inventory.report.show')), 1)
        ]),
        _: 1
      }, 8, ["disabled"])
    ])
  ]))
}
}

})